import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { useEffect, useState } from "react";

import PageHeaderProps from "../../../../model/PageHeaderProps";
import PageContainer from "../../../layout/PageContainer";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Container from "../../../layout/Container";
import LoadingSpinner from "../../../../assets/components/LoadingSpinner";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { FloatingLabel, Form } from "react-bootstrap";
import Header from "../../../layout/Header";
import { jobListPageStateSelector } from "../../../../store/selectors/jobSelector";
import { acknowledgeMerge, exportToActTrak, getPagedJobs, mergeJobs, resetJobListInvalidation, resetJobPage, deleteJob, renameJob } from "../../../../store/actions/jobActions";
import { Job } from "../../../../store/model/jobModel";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Fab from "@mui/material/Fab";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import Delete from "@mui/icons-material/Delete";
import ImportExport from "@mui/icons-material/ImportExport";
import Merge from "@mui/icons-material/Merge";
import Work from "@mui/icons-material/Work";

import MergeJobDialog from "./MergeJobDialog";
import { useTheme } from "styled-components";
import Puller from "../../../elements/Search/subcomponents/Puller";
import Client from "../../../../model/Client";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { rename } from "fs";



const JobListPage: React.FC<PageHeaderProps> = ({isNavOpen, setIsNavOpen}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const pageState = useAppSelector(jobListPageStateSelector);
    const mergeFinished = useAppSelector((state) => state.job.jobsPage.mergeComplete);
    const invalidateJobList = useAppSelector((state) => state.job.jobsPage.invalidateJobList);
    const [searchText, setSearchText] = useState<string>("");
    const [selectedJobs, setSelectedJobs] = useState<Job[]>([]);
    const [showMergeConfirmation, setShowMergeConfirmation] = useState<boolean>(false);
    const [openSelectionTray, setOpenSelectionTray] = useState<boolean>(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
    const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
    const [openRenameDialog, setOpenRenameDialog] = useState<boolean>(false);
    const [jobToRename, setJobToRename] = useState<Job | null>(null);
    const [renameValue, setRenameValue] = useState<string>("");

    useEffect(() =>{
        if(invalidateJobList){
            setSelectedJobs([]);
            dispatch(resetJobListInvalidation());
        }
    }, [invalidateJobList, dispatch]);

    useEffect(() => {
        dispatch(getPagedJobs({
            pageIndex: pageState.pageIndex,
            pageSize: pageState.pageSize,
            searchTerm: searchText
        }))

        return () => {
            dispatch(resetJobPage());
        }
    }, [dispatch, searchText]);

    useEffect(() => {
        if(mergeFinished){
            setSelectedJobs([]);
            dispatch(acknowledgeMerge());
        }
    }, [mergeFinished])


    const handleMerge = (newName: string, client: Client | null, invoiceClient: Client | null) => {
        dispatch(mergeJobs({
            MergeIds: selectedJobs.map(x => x.id),
            newJobName: newName,
            client: client,
            invoiceClient: invoiceClient
        }));

        setShowMergeConfirmation(false);
    }

    const openDelete = (job: Job) => {
        setJobToDelete(job);
        setOpenDeleteDialog(true);
    }

    const confirmDelete = async () => {
        if (!jobToDelete) return;
        await dispatch(deleteJob(jobToDelete.id));
        setOpenDeleteDialog(false);
        setJobToDelete(null);
        setSelectedJobs(selectedJobs.filter(x => x.id !== jobToDelete.id));
    }

    const openRename = (job: Job) => {
        setJobToRename(job);
        setRenameValue(job.name ?? "");
        setOpenRenameDialog(true);
    }

    const confirmRename = async () => {
        if (!jobToRename) return;
        const newName = renameValue.trim();
        if (newName.length === 0) return;
        if (newName === jobToRename.name) return;
        const payload = { jobId: jobToRename.id, newName };
        await dispatch(renameJob(payload));
        // update any selected jobs in the UI
        setSelectedJobs(selectedJobs.map(s => s.id === jobToRename.id ? { ...s, name: newName } : s));
        setOpenRenameDialog(false);
        setJobToRename(null);
        setRenameValue("");
    }

    return <PageContainer>
            <Box className="w-100" display="flex">
                <Box
                    sx={{
                        width: "80%",
                        position: "relative",
                        overflow: "auto",
                        maxHeight: "100vh",
                        [theme.breakpoints.down('lg')]: {
                            width: "100%"
                        },
                    }}
                >
                    <Header title={"Jobs"} isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen}>
                        <Box display={"flex"} className="w-100" justifyContent={"space-between"}>
                            <FloatingLabel
                                controlId="applicantSearch"
                                label="Search"
                                className="mb-3 text-dark"
                            >
                                <Form.Control
                                    placeholder="Search"
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </FloatingLabel>

                            <Button  
                                color="secondary"
                                variant="contained"
                                onClick={() => {
                                    setOpenSelectionTray(!openSelectionTray)
                                }}   
                                sx={{
                                    [theme.breakpoints.up('lg')]: {
                                        display: "none"
                                    },  
                                }}  
                                //AddIcon
                            >
                                <Work /> {selectedJobs.length > 0 ? "(" + selectedJobs.length + ")" : ""}
                            </Button>                         
                        </Box>
                    </Header>
                    <Container style={{ padding: "1rem", margin: "auto" }}>    
                    
                        {!pageState.loading && 
                            <TableContainer component={Card} sx={{ borderRadius: "1rem", background: "#fff", "tr:last-child td": { borderBottom: "0"}}}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Select</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Booking Dates</TableCell>
                                            <TableCell>Client</TableCell>
                                            <TableCell>Invoice Client</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pageState.data
                                            .map(x => (
                                                <TableRow                                                 
                                                    key={x.id}                                                 
                                                >
                                                    <TableCell>
                                                        <Checkbox 
                                                            checked={selectedJobs.some(y => y.id === x.id)} 
                                                            onChange={(e) => {
                                                                    if(e.currentTarget.checked){
                                                                        if(!selectedJobs.some(y => y.id === x.id)) {
                                                                            setSelectedJobs([...selectedJobs, x]);
                                                                        }
                                                                    } 
                                                                    else 
                                                                    {
                                                                        setSelectedJobs([...selectedJobs.filter(y => y.id !== x.id)]);
                                                                    }
                                                                }
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>{x.name}</TableCell>
                                                    <TableCell>
                                                        {Array.from(new Set(x.dates.map(date => new Date(date).toISOString().slice(0, 10))))
                                                            .sort()
                                                            .map(dateStr =>
                                                                new Date(dateStr).toLocaleDateString("en-GB", {
                                                                    day: "2-digit",
                                                                    month: "2-digit",
                                                                    year: "2-digit"
                                                                })
                                                            ).join(", ")
                                                        }
                                                    </TableCell>
                                                    <TableCell>{x.client?.code ?? "N/A"}</TableCell>                                                    
                                                    <TableCell>{x.invoiceClient?.code ?? "N/A"}</TableCell>
                                                    <TableCell>
                                                        <Box display="flex" gap={1}>
                                                            <Button size="small" variant="outlined" onClick={() => openRename(x)}>Rename</Button>
                                                            <Button size="small" variant="contained" color="error" onClick={() => openDelete(x)} startIcon={<Delete />}>Delete</Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        )}
                                        {pageState.data.length == 0 &&
                                            <TableRow                                      
                                                key={"NA"} 
                                            >
                                                <TableCell colSpan={2}> No jobs found {(searchText.length > 0 ? "with name '" + searchText + "'" : "")}</TableCell>
                                            </TableRow>
                                        }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        }
                        
                        
                        { pageState.loading &&
                            <Box display="flex" justifyContent="center" p="3">
                                <LoadingSpinner style={{ width: "4rem", height: "4rem"}}/>
                            </Box>
                        } 

                        <MergeJobDialog
                            show={showMergeConfirmation}
                            handleModalClose={() => setShowMergeConfirmation(false)}
                            selectedJobs={selectedJobs}
                            handleConfirmation={handleMerge}
                        />
                        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
                            <DialogTitle>Delete Job</DialogTitle>
                            <DialogContent>
                                <Typography>Are you sure you want to delete the job "{jobToDelete?.name}"?</Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
                                <Button color="error" onClick={confirmDelete} variant="contained">Delete</Button>
                            </DialogActions>
                        </Dialog>

                        <Dialog open={openRenameDialog} onClose={() => setOpenRenameDialog(false)}>
                            <DialogTitle>Rename Job</DialogTitle>
                            <DialogContent sx={{ pt: 2, pb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="New name"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.currentTarget.value)}
                                    margin="dense"
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenRenameDialog(false)}>Cancel</Button>
                                <Button color="primary" onClick={confirmRename} variant="contained" disabled={renameValue.trim().length === 0 || renameValue === jobToRename?.name}>Rename</Button>
                            </DialogActions>
                        </Dialog>
                            
                    </Container>
                </Box>
                <Box sx={{ 
                    width: "28%" ,
                    height: "100vh",
                    background: "white",
                    overflowY: "auto",
                    overflowX: "hidden",
                    [theme.breakpoints.down('lg')]: {
                        display: "none"
                    },
                }}>
                   <JobSelectionPanel 
                        selectedJobs={selectedJobs}
                        setSelectedJobs={setSelectedJobs}
                        setShowMergeConfirmation={setShowMergeConfirmation}
                    />
                </Box>

                <SwipeableDrawer
                    anchor={'bottom'}
                    open={openSelectionTray}
                    onClose={() => setOpenSelectionTray(false)}
                    onOpen={() => setOpenSelectionTray(true)}
                    sx={{  
                        zIndex: "1049", //Allow modals to appear over the drawer                       
                        [theme.breakpoints.up('lg')]: {
                            display: "none"
                        },  
                    }}
                >
                    <Box sx={{
                        height: "90vh",
                        p: 4,
                        background: theme.palette.common.white,
                        overflowY: "auto"
                    }}>
                        <Puller />
                        <JobSelectionPanel 
                            selectedJobs={selectedJobs}
                            setSelectedJobs={setSelectedJobs}
                            setShowMergeConfirmation={setShowMergeConfirmation}
                        />
                    </Box>
                </SwipeableDrawer>
            </Box>
        </PageContainer>        
}

interface JobSelectionPanelProps {
    selectedJobs: Job[];
    setSelectedJobs: (jobs: Job[]) => void;
    setShowMergeConfirmation: (show: boolean) => void;
}


const JobSelectionPanel: React.FC<JobSelectionPanelProps> = ({selectedJobs, setSelectedJobs, setShowMergeConfirmation}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const [exporting, setExporting] = useState(false);

    const handleExport = async () => {
        setExporting(true);
        try {
            await dispatch(exportToActTrak({ exportIds: selectedJobs.map(x => x.id) }));
        } finally {
            setExporting(false);
        }
    };

    return <Box sx={{ width: '100%', padding: "1rem" }}>
        <Typography variant="h5" sx={{ marginBottom: "1rem" }}>Selected Jobs</Typography>
        <ul style={{ marginLeft: "0", paddingLeft: "0", paddingBottom: "3rem" }}>
            {selectedJobs.map(job => (
                <li key={job.id} style={{ listStyle: "none", borderBottom: "1px solid rgba(0,0,0,0.05)", padding: "0.5rem 0" }}>
                    <ListItemButton sx={{ "&:hover .del-icon": { display: "block" } }}>
                        <ListItemText primary={job.name} />
                        <ListItemIcon className={"del-icon"} sx={{ display: exporting ? "none" : "none" }} onClick={exporting ? undefined : (e) => setSelectedJobs(selectedJobs.filter(x => x.id !== job.id))}>
                            {!exporting && <Delete />}
                        </ListItemIcon>
                    </ListItemButton>
                </li>
            ))}
        </ul>
        <Box sx={{ display: "flex", position: "absolute", bottom: "2rem", right: "2rem", gap: "1rem", [theme.breakpoints.down('lg')]: { flexWrap: "wrap", right: "1rem", left: "1rem", "& button": { width: "100%" } } }}>
            {selectedJobs.length > 0 &&
                <Fab variant="extended" color="success" onClick={handleExport} disabled={exporting}>
                    {exporting ? <LoadingSpinner style={{ width: 24, height: 24, marginRight: 8 }} /> : <ImportExport />}
                    {exporting ? "Exporting..." : "Export to ActTrak"}
                </Fab>
            }
            {selectedJobs.length > 1 &&
                <Fab
                    variant="extended"
                    color="secondary"
                    onClick={() => setShowMergeConfirmation(true)}
                    disabled={exporting}
                ><Merge /> Merge Jobs ({selectedJobs.length})</Fab>
            }
        </Box>
    </Box>;
}

export default JobListPage;